import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import HistoryIcon from "@material-ui/icons/History";
import { formatBcomeVersionLabel } from "../../../components/DbVersion";
import { LcaVersionStatusIcon } from "../../../components/LcaVersionStatus";
import { hostname } from "../../../providers/api";

// Same size as the versions table's column headers.
const HEADER_TEXT = { fontSize: "0.875rem", fontWeight: 500 };

const lcaCalculatedWithLabel = (versionSet) => {
  if (!versionSet) return null;
  return [
    versionSet.lcaMethodologyVersion?.name && `LCA ${versionSet.lcaMethodologyVersion.name}`,
    versionSet.databaseVersion?.name && `DB ${versionSet.databaseVersion.name}`,
  ]
    .filter(Boolean)
    .join(" · ");
};

// One column per version of the project. `inVersionSet` reads that version from a
// Version Set: the one a traceability upload in progress moves the project to.
export const VERSION_COLUMNS = [
  {
    key: "traceability",
    label: "Version",
    current: (record) => record.version,
    inVersionSet: (versionSet) => versionSet?.description,
  },
  {
    key: "database",
    label: "DB",
    current: (record, versionSets) =>
      versionSets[record.bcomeVersionId]?.databaseVersion?.name,
    inVersionSet: (versionSet) => versionSet?.databaseVersion?.name,
  },
  {
    key: "lca",
    label: "LCA",
    current: (record, versionSets) =>
      versionSets[record.bcomeVersionId]?.lcaMethodologyVersion?.name,
    inVersionSet: (versionSet) => versionSet?.lcaMethodologyVersion?.name,
  },
  {
    key: "circularity",
    label: "Circularity",
    current: (record, versionSets) =>
      versionSets[record.bcomeVersionId]?.circularityVersion?.circularity_version_name,
    inVersionSet: (versionSet) => versionSet?.circularityVersion?.circularity_version_name,
  },
  {
    key: "lastUpload",
    label: "Last traceability upload",
    current: () => null,
  },
  {
    key: "lastLca",
    label: "Last LCA calculated with",
    current: (record, versionSets) =>
      lcaCalculatedWithLabel(versionSets[record.lcaBcomeVersionId]),
  },
];

const formatDay = (value) => (value ? new Date(value).toLocaleDateString("es-ES") : null);

// A Version Set from GET /projects/:id/versionChanges, labelled as everywhere else in
// Admin: "LCA 26.3 · DB 1.8 · Circularity 1.0  (2.0)".
export const versionSetLabel = (versionSet) =>
  formatBcomeVersionLabel({
    id: versionSet.bcomeVersionId,
    description: versionSet.description,
    lcaMethodologyVersion: versionSet.lcaVersion ? { name: versionSet.lcaVersion } : null,
    databaseVersion: versionSet.databaseVersion ? { name: versionSet.databaseVersion } : null,
    circularityVersion: versionSet.circularityVersion
      ? { circularity_version_name: versionSet.circularityVersion }
      : null,
  });

/**
 * The Version Sets the project has had, newest first, from its changes (newest first):
 * each with the date it was applied (`from`) and the date it was replaced (`until`, null
 * for the current one). The Version Set before the first recorded change was applied
 * before the history existed, so its `from` is unknown.
 */
export const buildVersionSetTimeline = (changes) => {
  const timeline = changes.map((change, index) => ({
    key: change.id,
    versionSet: change.newVersion,
    from: change.changedAt,
    until: index === 0 ? null : changes[index - 1].changedAt,
    user: change.user,
    upload: change.upload,
  }));
  const oldest = changes[changes.length - 1];
  if (oldest?.previousVersion) {
    timeline.push({
      key: `${oldest.id}-previous`,
      versionSet: oldest.previousVersion,
      from: null,
      until: oldest.changedAt,
      user: null,
      upload: null,
    });
  }
  return timeline.filter((entry) => entry.versionSet);
};

const fetchVersionChanges = async (projectId, accessToken) => {
  const response = await fetch(`${hostname}/projects/${projectId}/versionChanges`, {
    headers: { authorization: "Bearer " + accessToken },
  });
  if (response.status === 404) {
    throw new Error("Version history is not available in this backend yet.");
  }
  if (response.status >= 400) {
    throw new Error(`Version history request failed (HTTP ${response.status})`);
  }
  return response.json();
};

const formatDate = (value) => (value ? new Date(value).toLocaleString() : null);

/**
 * Version Sets of the project over time (GET /projects/:id/versionChanges): which one it
 * had, from when until when, and who applied it with which upload.
 */
export const VersionHistoryDialog = ({ open, onClose, projectId, accessToken }) => {
  const [state, setState] = useState({ loading: false, error: null, changes: [] });

  useEffect(() => {
    if (!open || !projectId) return undefined;
    let cancelled = false;
    setState({ loading: true, error: null, changes: [] });
    fetchVersionChanges(projectId, accessToken)
      .then((changes) => {
        if (!cancelled) setState({ loading: false, error: null, changes: changes || [] });
      })
      .catch((error) => {
        if (!cancelled) setState({ loading: false, error: error.message, changes: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [open, projectId, accessToken]);

  const { loading, error, changes } = state;
  const timeline = buildVersionSetTimeline(changes);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Version Set history</DialogTitle>
      <DialogContent>
        {loading ? (
          <CircularProgress size={24} />
        ) : error ? (
          <p style={{ color: "#d32f2f" }}>{error}</p>
        ) : timeline.length === 0 ? (
          <p>
            No Version Set changes recorded. Changes are recorded from traceability uploads
            since this history was introduced.
          </p>
        ) : (
          <Table size="small" aria-label="Version Set history">
            <TableHead>
              <TableRow>
                <TableCell>Version Set</TableCell>
                <TableCell>From</TableCell>
                <TableCell>Until</TableCell>
                <TableCell>Applied by</TableCell>
                <TableCell>Upload</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {timeline.map((entry) => (
                <TableRow key={entry.key}>
                  <TableCell>
                    {entry.until === null ? (
                      <strong>{versionSetLabel(entry.versionSet)}</strong>
                    ) : (
                      versionSetLabel(entry.versionSet)
                    )}
                  </TableCell>
                  <TableCell>{formatDate(entry.from) || "Before history"}</TableCell>
                  <TableCell>{formatDate(entry.until) || "Current"}</TableCell>
                  <TableCell>{entry.user?.name || "-"}</TableCell>
                  <TableCell style={{ wordBreak: "break-word" }}>
                    {entry.upload?.filename || "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const fetchJsonOrNull = (url, accessToken) =>
  fetch(url, { headers: { authorization: "Bearer " + accessToken } })
    .then((response) => (response.ok ? response.json() : null))
    .catch(() => null);

// The project's last traceability upload and its stored LCA results: the version
// check the Projects list shows (traceability version vs. the version they were
// calculated with) and the date of the last calculation. Refetched when the record
// changes (upload, calculation, rollback) and when an upload starts or ends.
const useLatestActivity = (record, accessToken, pendingBcomeVersionId) => {
  const [latest, setLatest] = useState({ lcaStatus: null, calculatedAt: null, upload: null });
  const projectId = record?.id;
  const updatedAt = record?.updatedAt;

  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;
    Promise.all([
      fetchJsonOrNull(`${hostname}/lca-supply/lca-version-status/${projectId}`, accessToken),
      fetchJsonOrNull(`${hostname}/lca-supply/last-calculation/${projectId}`, accessToken),
      fetchJsonOrNull(`${hostname}/projects/${projectId}/lastTraceabilityUpload`, accessToken),
    ]).then(([lcaStatus, lastCalculation, lastUpload]) => {
      if (cancelled) return;
      setLatest({
        lcaStatus,
        calculatedAt: lastCalculation?.lastCalculation?.runTimestamp ?? null,
        upload: lastUpload?.upload ?? null,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [projectId, updatedAt, accessToken, pendingBcomeVersionId]);

  return latest;
};

// "27/10/2026", with the outcome when the upload did not succeed.
export const lastUploadLabel = (upload) => {
  const day = formatDay(upload?.uploadDate);
  if (!day) return null;
  if (upload.status === "FAILED") return `${day} · failed`;
  if (upload.status === "PROCESSING" || upload.status === "UPLOADED") return `${day} · in progress`;
  return day;
};

// A version that a traceability upload in progress is changing: "old → new".
const PendingValue = ({ current, next }) => (
  <span data-testid="version-pending">
    <span style={{ color: "#6b6b80" }}>{current || "-"}</span> → {next || "-"}
  </span>
);

/**
 * The project's versions in one table, with the history of its Version Sets. While a
 * traceability upload runs (pendingBcomeVersionId), the versions it changes show
 * "old → new"; the project only gets them if the upload succeeds.
 */
const ProjectVersionsSection = ({ record, versionSets, accessToken, pendingBcomeVersionId }) => {
  const [historyOpen, setHistoryOpen] = useState(false);
  const latest = useLatestActivity(record, accessToken, pendingBcomeVersionId);
  if (!record) return null;

  const sets = versionSets || {};
  const pendingVersionSet =
    pendingBcomeVersionId && pendingBcomeVersionId !== record.bcomeVersionId
      ? sets[pendingBcomeVersionId]
      : null;

  const renderValue = (column) => {
    const current = column.current(record, sets);
    if (pendingVersionSet && column.inVersionSet) {
      const next = column.inVersionSet(pendingVersionSet);
      if (next !== current) return <PendingValue current={current} next={next} />;
    }
    if (column.key === "lastUpload") {
      return lastUploadLabel(latest.upload) || "-";
    }
    if (column.key === "lastLca") {
      const calculatedAt = formatDay(latest.calculatedAt);
      // No LCA results: a dash, not the mismatch alert.
      if (!current && !calculatedAt) return "-";
      return (
        <>
          {[current, calculatedAt].filter(Boolean).join(" · ")}
          {/* Compared against the project's current version: not meaningful mid-upload. */}
          {!pendingBcomeVersionId && latest.lcaStatus?.status !== "no_calculation" && (
            <LcaVersionStatusIcon lcaVersionStatus={latest.lcaStatus} />
          )}
        </>
      );
    }
    return current || "-";
  };

  return (
    <div style={{ margin: "8px 0 16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <h3 style={{ margin: 0, ...HEADER_TEXT }}>Versions</h3>
        {pendingBcomeVersionId && (
          <span
            data-testid="versions-uploading"
            style={{ ...HEADER_TEXT, fontWeight: 400, fontStyle: "italic", color: "#6b6b80" }}
          >
            uploading…
          </span>
        )}
        <Button
          size="small"
          startIcon={<HistoryIcon style={{ fontSize: 16 }} />}
          onClick={() => setHistoryOpen(true)}
          style={{ ...HEADER_TEXT, textTransform: "none" }}
        >
          History
        </Button>
      </div>
      <Table size="small" aria-label="Project versions" style={{ width: "auto" }}>
        <TableHead>
          <TableRow>
            {VERSION_COLUMNS.map((column) => (
              <TableCell key={column.key}>{column.label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            {VERSION_COLUMNS.map((column) => (
              <TableCell key={column.key}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  {renderValue(column)}
                </span>
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
      <VersionHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        projectId={record.id}
        accessToken={accessToken}
      />
    </div>
  );
};

export default ProjectVersionsSection;
